import React, { useState, useEffect, useContext } from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import axiosInstance from "../axiosInstance";
import AuthContext from "../AuthContext";

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const StatBox = ({ color, icon, label, value, sub }) => (
  <div className={`small-box bg-${color}`}>
    <div className="inner">
      <h3 style={{ fontSize: "1.8rem" }}>{value}</h3>
      <p style={{ fontSize: "13px", marginBottom: 2 }}>{label}</p>
      {sub && <small style={{ fontSize: "11px", opacity: 0.85 }}>{sub}</small>}
    </div>
    <div className="icon">
      <i className={`fas fa-${icon}`} />
    </div>
  </div>
);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function BasicDashboard() {
  const { planName, planEndDate } = useContext(AuthContext);
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [chartMode, setChartMode] = useState("value"); // 'value' | 'count'

  useEffect(() => {
    axiosInstance
      .get("/dashboard/basic-stats")
      .then((res) => setData(res.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const companyName = localStorage.getItem("companyname") || "Dashboard";

  const chartData = data
    ? {
        labels: MONTHS,
        datasets: [
          {
            label: chartMode === "value" ? "Sales Value (₹)" : "Sales Count",
            data: chartMode === "value" ? data.chart.value : data.chart.count,
            backgroundColor: "rgba(0, 123, 255, 0.65)",
            borderColor: "#007bff",
            borderWidth: 1,
            borderRadius: 4,
          },
        ],
      }
    : null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            chartMode === "value"
              ? `₹${(ctx.parsed.y || 0).toLocaleString("en-IN")}`
              : `${ctx.parsed.y} sales`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (v) =>
            chartMode === "value"
              ? `₹${(v / 1000).toFixed(0)}k`
              : v,
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <section className="content d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-2" role="status" />
            <p className="text-muted">Loading dashboard...</p>
          </div>
        </section>
      </div>
    );
  }

  const potentialProfit = (data?.stock_sell_value || 0) - (data?.stock_buy_value || 0);

  return (
    <div className="content-wrapper">
      {/* Header */}
      <div className="content-header">
        <div className="container-fluid">
          <div className="row align-items-center mb-2">
            <div className="col-sm-7">
              <h1 className="m-0">
                <i className="fas fa-tachometer-alt text-primary mr-2" />
                {companyName}
              </h1>
              {planName && (
                <small className="text-muted">
                  <i className="fas fa-crown text-warning mr-1" />
                  {planName}
                  {planEndDate && (
                    <span className="ml-2">
                      &bull; Valid until{" "}
                      {new Date(planEndDate).toLocaleDateString("en-GB")}
                    </span>
                  )}
                </small>
              )}
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item active">Dashboard</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">

          {/* ── Stock Section ─────────────────────────────────── */}
          <div className="row">
            <div className="col-12 mb-1">
              <h6 className="text-muted text-uppercase font-weight-bold" style={{ fontSize: "11px", letterSpacing: "1px" }}>
                <i className="fas fa-warehouse mr-1" /> Inventory / Stock
              </h6>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-3 col-md-6 col-sm-6">
              <StatBox
                color="info"
                icon="motorcycle"
                label="Total Vehicles in Stock"
                value={data?.stock_count ?? "—"}
              />
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6">
              <StatBox
                color="primary"
                icon="rupee-sign"
                label="Stock Purchase Value"
                value={fmt(data?.stock_buy_value)}
                sub="Total cost of all stock"
              />
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6">
              <StatBox
                color="teal"
                icon="tags"
                label="Stock Selling Value"
                value={fmt(data?.stock_sell_value)}
                sub="Expected sale price total"
              />
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6">
              <StatBox
                color={potentialProfit >= 0 ? "success" : "danger"}
                icon="chart-line"
                label="Potential Profit"
                value={fmt(potentialProfit)}
                sub="Sell value − Purchase cost"
              />
            </div>
          </div>

          {/* ── Sales Section ─────────────────────────────────── */}
          <div className="row mt-2">
            <div className="col-12 mb-1">
              <h6 className="text-muted text-uppercase font-weight-bold" style={{ fontSize: "11px", letterSpacing: "1px" }}>
                <i className="fas fa-file-invoice-dollar mr-1" /> Sales Overview
              </h6>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-3 col-md-6 col-sm-6">
              <StatBox
                color="warning"
                icon="sun"
                label="Today's Sales"
                value={data?.sales_today_count ?? 0}
                sub={fmt(data?.sales_today_value)}
              />
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6">
              <StatBox
                color="danger"
                icon="calendar-alt"
                label="This Month's Sales"
                value={data?.sales_month_count ?? 0}
                sub={fmt(data?.sales_month_value)}
              />
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6">
              <StatBox
                color="success"
                icon="calendar-check"
                label="This Year's Sales"
                value={data?.sales_year_count ?? 0}
                sub={fmt(data?.sales_year_value)}
              />
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6">
              <StatBox
                color="secondary"
                icon="percentage"
                label="Avg Sale Value"
                value={
                  data?.sales_year_count
                    ? fmt(data.sales_year_value / data.sales_year_count)
                    : "—"
                }
                sub="Per vehicle this year"
              />
            </div>
          </div>

          {/* ── Monthly Sales Chart ───────────────────────────── */}
          <div className="row mt-3">
            <div className="col-12">
              <div className="card card-outline card-primary elevation-2">
                <div className="card-header d-flex align-items-center">
                  <h3 className="card-title">
                    <i className="fas fa-chart-bar mr-2" />
                    Monthly Sales — {new Date().getFullYear()}
                  </h3>
                  <div className="card-tools ml-auto">
                    <div className="btn-group btn-group-sm">
                      <button
                        className={`btn ${chartMode === "value" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setChartMode("value")}
                      >
                        Value
                      </button>
                      <button
                        className={`btn ${chartMode === "count" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setChartMode("count")}
                      >
                        Count
                      </button>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {chartData ? (
                    <Bar data={chartData} options={chartOptions} height={80} />
                  ) : (
                    <p className="text-muted text-center">No sales data available.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Month Breakdown Table ─────────────────────────── */}
          {data?.chart && (
            <div className="row mt-1">
              <div className="col-12">
                <div className="card card-outline card-secondary elevation-1">
                  <div className="card-header">
                    <h3 className="card-title">
                      <i className="fas fa-table mr-2" />
                      Month-wise Breakdown
                    </h3>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-sm table-striped table-hover mb-0">
                        <thead className="thead-dark">
                          <tr>
                            <th>Month</th>
                            <th className="text-center">Sales Count</th>
                            <th className="text-right">Sales Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {MONTHS.map((m, i) => (
                            <tr key={m}>
                              <td>{m}</td>
                              <td className="text-center">
                                {data.chart.count[i] > 0 ? (
                                  <span className="badge badge-info">{data.chart.count[i]}</span>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="text-right">
                                {data.chart.value[i] > 0 ? fmt(data.chart.value[i]) : <span className="text-muted">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="thead-light">
                          <tr>
                            <th>Total</th>
                            <th className="text-center">
                              <strong>{data.chart.count.reduce((a, b) => a + b, 0)}</strong>
                            </th>
                            <th className="text-right">
                              <strong>{fmt(data.chart.value.reduce((a, b) => a + b, 0))}</strong>
                            </th>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>
    </div>
  );
}
